import React, { useContext, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

// Styles
import { Container, Text } from "./styles";
import "react-dropdown/style.css";

import * as materialsActions from "../../../store/modules/materials/actions";

import { contractContext } from "../../../Contexts/contractContext";

import Loader from "../../../components/Loader";
import EditMaterials from "./components/EditMaterial";
import api from "../../../services/api";

const Materials = (props) => {
  const brandColor = useSelector(
    (state) => state.contractReducer.contract.config.brandColor
  );
  const backgroundColor = useSelector(
    (state) => state.contractReducer.contract.config.backgroundColor
  );
  const fontColor = useSelector(
    (state) => state.contractReducer.contract.config.fontColor
  );

  const { currentContract, setNewMaterialList } = useContext(contractContext);

  const dispatch = useDispatch();

  const brands = useSelector((state) => state.materialsReducer.brands);
  const isBrandsLoading = useSelector(
    (state) => state.materialsReducer.isBrandsLoading
  );
  // Cache { [marca]: Material[] } — preenchido sob demanda.
  const byBrand = useSelector((state) => state.materialsReducer.byBrand);
  const loadingBrands = useSelector(
    (state) => state.materialsReducer.loadingBrands
  );

  const [filtedBrands, setFilteredBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);

  const [currentMaterials, setCurrentMaterials] = useState([]);
  const [currentMaterialsFiltered, setCurrentMaterialsFiltered] = useState([]);

  const [selectAllChecked, setSelectAllChecked] = useState(false);

  const [selectedMaterials, setSelectedMaterials] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  const [showEditMaterial, setShowEditMaterial] = useState(false);
  const [idMaterialEdit, setidMaterialEdit] = useState();

  const [searchBrandInputText, setSearchBrandInputText] = useState("");

  const [reproductions, setReproductions] = useState();
  const [surfaces, setSurfaces] = useState();
  const [types, setTypes] = useState();

  const [isLoadingAttributes, setIsLoadingAttributes] = useState(true);
  const [refreshRequested, setRefreshRequested] = useState(false);

  useEffect(() => {
    if (currentContract.materials) {
      const initialList = currentContract.materials.map((m) => m.material);
      setSelectedMaterials(initialList);
    }
  }, [currentContract]);

  const loadAttributes = () => {
    setIsLoadingAttributes(true);

    return api
      .get("/allAttributes")
      .then((i) => {
        const reproduction = [];
        const surface = [];
        const type = [];

        i.data.reproductions.forEach((element) => {
          reproduction.push(element.reproduction);
        });
        i.data.surfaces.forEach((element) => {
          surface.push(element.surface);
        });
        i.data.types.forEach((element) => {
          type.push(element.type);
        });

        setReproductions(reproduction);
        setSurfaces(surface);
        setTypes(type);
      })
      .finally(() => setIsLoadingAttributes(false));
  };

  // Marcas e atributos são independentes e pequenos, então vão juntos no mount.
  // Antes o /allAttributes só disparava depois que o catálogo inteiro (~14 MB)
  // chegava, porque estava preso a um efeito com guarda `materials.length > 0`.
  useEffect(() => {
    dispatch(materialsActions.getMaterialBrands());
    loadAttributes();
  }, []);

  // `loadingBrands` sozinho não basta: entre marcar a marca (ou limpar o
  // cache) e a saga registrar a busca há um render em que nada está "em voo"
  // e a grade piscaria a mensagem de lista vazia.
  const hasPendingBrandLoad =
    loadingBrands.length > 0 || selectedBrands.some((b) => !byBrand[b]);

  // O refresh só acaba quando nada mais está em voo: atributos, marcas e os
  // materiais de cada marca marcada.
  const isRefreshing =
    refreshRequested &&
    (isLoadingAttributes || isBrandsLoading || hasPendingBrandLoad);

  useEffect(() => {
    if (refreshRequested && !isRefreshing) setRefreshRequested(false);
  }, [refreshRequested, isRefreshing]);

  // Descarta o cache por marca e rebusca marcas, materiais e atributos. As
  // marcas marcadas continuam marcadas: o efeito de carga sob demanda enxerga
  // o cache vazio e refaz as buscas sozinho.
  const handleRefresh = () => {
    if (isRefreshing) return;

    setRefreshRequested(true);
    dispatch(materialsActions.invalidateMaterialsCache());
    dispatch(materialsActions.getMaterialBrands());
    loadAttributes();
  };

  useEffect(() => {
    setIsLoading(isBrandsLoading);
  }, [isBrandsLoading]);

  useEffect(() => {
    setFilteredBrands(brands);
  }, [brands]);

  // Busca os materiais das marcas recém-marcadas que ainda não estão em cache.
  useEffect(() => {
    const missing = selectedBrands.filter(
      (b) => !byBrand[b] && !loadingBrands.includes(b)
    );

    if (missing.length > 0) {
      dispatch(materialsActions.getMaterialsByBrands(missing));
    }
  }, [selectedBrands, byBrand, loadingBrands]);

  // Remonta a grade sempre que a seleção muda ou uma marca termina de carregar.
  useEffect(() => {
    handleBrandFilter(selectedBrands);
  }, [selectedBrands, byBrand]);

  useEffect(() => {
    if (reproductions) handleSort(reproductions);

    if (surfaces) handleSort(surfaces);

    if (types) handleSort(types);
  }, [reproductions, surfaces, types]);

  const handleSort = (array) => {
    if (array)
      array.sort(function (a, b) {
        if (a.description > b.description) {
          return 1;
        }
        if (a.description < b.description) {
          return -1;
        }
        return 0;
      });
  };

  const handleSelectedMaterials = (materialId, checked) => {
    let mHelper = selectedMaterials;

    if (checked) {
      if (!mHelper.includes(materialId)) mHelper.push(materialId);
    } else {
      mHelper.splice(mHelper.indexOf(materialId), 1);
    }

    setSelectedMaterials(mHelper);
    setNewMaterialList(mHelper);
  };

  // Ao voltar da edição de um material a página é remontada; restauramos as
  // marcas que estavam marcadas. Depende só da lista de marcas — os materiais
  // vêm depois, pelo efeito de carga sob demanda.
  useEffect(() => {
    const saved = localStorage.getItem("brandsFilter");

    if (saved && brands.length !== 0) {
      setSelectedBrands(JSON.parse(saved));

      localStorage.removeItem("brandsFilter");
    }
  }, [brands]);

  const handleBrandCheck = (target) => {
    const next = target.checked
      ? [...selectedBrands, target.value]
      : selectedBrands.filter((b) => b !== target.value);

    if (target.checked) setSelectAllChecked(false);

    setSelectedBrands(next);
  };

  const handleBrandFilter = (sbHelper) => {
    const mHelper = sbHelper
      .flatMap((b) => byBrand[b] || [])
      .map((m) => {
        return {
          id: m._id,
          miniThumbPath: m.miniThumbPath,
          description: m.description,
          brand: m.brand,
          format: m.format,
          isChecked: selectedMaterials.includes(m._id),
        };
      });

    // O comparador antigo devolvia `true` para empates, o que fazia a ordem
    // final depender da ordem de entrada. Como os materiais agora chegam
    // agrupados por marca (e não mais na ordem do catálogo inteiro), o
    // desempate precisa ser explícito para a grade ficar estável.
    mHelper.sort(function (a, b) {
      if (a.description < b.description) return -1;
      if (a.description > b.description) return 1;
      return 0;
    });

    setCurrentMaterials([...new Set(mHelper)]);
    setCurrentMaterialsFiltered([...new Set(mHelper)]);
  };

  const handleSelectAllMaterials = (target) => {
    let mHelper = [...new Set(currentMaterials)];

    setSelectAllChecked(target.checked);

    mHelper.forEach((m) => {
      m.isChecked = target.checked;
      handleSelectedMaterials(m.id, target.checked);
    });

    setCurrentMaterials(mHelper);
    setCurrentMaterialsFiltered(mHelper);
  };

  const handleCheckChildElement = (target) => {
    const mHelper = [...new Set(currentMaterials)];

    mHelper.forEach((m) => {
      if (m.id == target.value) {
        handleSelectedMaterials(m.id, target.checked);
        return (m.isChecked = target.checked);
      }
    });

    setCurrentMaterials(mHelper);
  };

  const filterRow = (a) => {
    var list = new Array();

    for (const i of currentMaterials) {
      if (
        i.description.toLowerCase().includes(a.toLowerCase()) ||
        i.format.toLowerCase().includes(a.toLowerCase())
      ) {
        list.push(i);
      }
    }

    setCurrentMaterialsFiltered(list);
  };

  useEffect(() => {
    if (searchBrandInputText !== "") {
      const newFilteredBrand = [];
      brands.forEach((brand) => {
        if (brand.toLowerCase().includes(searchBrandInputText.toLowerCase())) {
          newFilteredBrand.push(brand);
        }
      });
      setFilteredBrands(newFilteredBrand);
    } else {
      setFilteredBrands(brands);
    }
  }, [searchBrandInputText]);
  const handleBrandInput = (event) => {
    setSearchBrandInputText(event.target.value);
    return;
  };
  return (
    <Container
      backgroundColor={backgroundColor}
      fontColor={fontColor}
      brandColor={brandColor}
    >
      <div className="materials-header">
        <h1>Materiais</h1>
        <button
          type="button"
          className="refresh-btn"
          onClick={handleRefresh}
          disabled={isRefreshing}
          title="Atualizar materiais"
        >
          <i className={`fas fa-sync-alt ${isRefreshing ? "fa-spin" : ""}`}></i>
        </button>
      </div>
      <div className="lists">
        {isLoading ? (
          <Loader borderRadius={"15px"} />
        ) : (
          <>
            <div className="brands">
              <h3>Marcas</h3>
              <input
                type="text"
                onChange={handleBrandInput}
                value={searchBrandInputText}
              ></input>
              <div className="brand-list">
                {filtedBrands.map((b, index) => (
                  <button key={index}>
                    <input
                      type="checkbox"
                      value={b}
                      checked={selectedBrands.includes(b)}
                      onChange={(e) => handleBrandCheck(e.target)}
                    />
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <div className="material-list-header">
              <div className="selecionar-todos-checkbox">
                <form className="form">
                  <label>Pesquisar </label>
                  <input
                    type="text"
                    placeholder="Pesquisar"
                    onChange={(event) => {
                      filterRow(event.target.value);
                    }}
                  />
                </form>

                <div className="space"></div>

                <div className="form">
                  <input
                    type="checkbox"
                    checked={selectAllChecked}
                    onChange={(e) => handleSelectAllMaterials(e.target)}
                  />
                  Selecionar todos
                  {/* <button onClick={() => testList()}>TESTE</button> */}
                </div>
              </div>

              {hasPendingBrandLoad && currentMaterialsFiltered.length <= 0 ? (
                <Loader borderRadius={"15px"} />
              ) : currentMaterialsFiltered.length <= 0 ? (
                <Text>Selecione as marcas que deseja incluir no contrato</Text>
              ) : (
                <div className="material-list">
                  {currentMaterialsFiltered.map((m) => (
                    <div key={m.id} className="list__item">
                      <img
                        className="list__item__img"
                        loading="lazy"
                        quality={50}
                        src={m.miniThumbPath ? m.miniThumbPath : m.thumbPath}
                        alt={m.description}
                      />
                      <h2 className="list__item__title">{m.description}</h2>
                      <p className="list__item__text">{m.brand}</p>
                      <p className="list__item__text">{m.format}</p>
                      <input
                        type="checkbox"
                        value={m.id}
                        className="checkbox"
                        onChange={(t) => handleCheckChildElement(t.target)}
                        checked={m.isChecked}
                      ></input>
                      <span
                        onClick={() => {
                          setShowEditMaterial(true);
                          setidMaterialEdit(m.id);
                        }}
                      >
                        <i className="fas fa-pen"></i>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <EditMaterials
        showEditMaterial={showEditMaterial}
        setShowEditMaterial={setShowEditMaterial}
        idMaterial={idMaterialEdit}
        brands={brands}
        reproductions={reproductions}
        surfaces={surfaces}
        types={types}
        currentMaterialsFiltered={currentMaterialsFiltered}
        handleMainSessionClick={props.handleMainSessionClick}
        selectedBrands={selectedBrands}
      />
    </Container>
  );
};

export default Materials;
